// SETTINGS V2 — MAP-A-FE2 map pin (§5.3). Custom OverlayViewF teardrop pin —
// technique from serviceBoard/CityMarker (OverlayViewF + OVERLAY_MOUSE_TARGET +
// centering getPixelPositionOffset), styling authored fresh in v2 scoped tokens.
// Variants derive from the Location itself: parent vs child (parentLocationId),
// default (isDefault → accent ring), disabled (isDisabled → muted + dimmed; only
// present when the eye-toggle reveals inactive rows). Inline styles + CSS-var
// tokens only. No MUI/dt/Tailwind. SEC-REQ-2: no import-time side effects.
import React, { useCallback, useMemo, useState } from "react";
import { OverlayView, OverlayViewF } from "@react-google-maps/api";

import type { Location } from "../../../machinery/components/locations/locationTypes";

export interface LocationsMapPinV2Props {
  /** Must have non-null latitude/longitude (the map pre-filters located nodes). */
  location: Location;
  onClick: (loc: Location) => void;
  /** Selected = focused via a row/map pin click → turns red + pops (§enhancement). */
  selected?: boolean;
}

const LocationsMapPinV2: React.FC<LocationsMapPinV2Props> = ({ location, onClick, selected }) => {
  const [hover, setHover] = useState(false);

  const position = useMemo(
    () => ({ lat: location.latitude as number, lng: location.longitude as number }),
    [location.latitude, location.longitude]
  );

  const isChild = !!location.parentLocationId;
  const isDefault = location.isDefault;
  const isDisabled = location.isDisabled;
  const size = isChild ? 22 : 30;

  // Center the pin's bounding box on the coordinate (§5.3 "centering").
  const getPixelPositionOffset = useCallback(
    () => ({ x: -(size / 2), y: -(size / 2) }),
    [size]
  );

  // Colour by variant. Selected (red) OVERRIDES every other variant incl. the
  // disabled dim; otherwise disabled recolours to muted + dims (§5.5).
  let bg: string;
  let borderColor: string;
  let glyphColor: string;
  if (selected) {
    bg = "var(--sv2-pin-sel)";
    borderColor = "var(--sv2-pin-sel)";
    glyphColor = "#FFFFFF";
  } else if (isDisabled) {
    bg = isChild ? "var(--card)" : "var(--muted)";
    borderColor = "var(--muted)";
    glyphColor = isChild ? "var(--muted)" : "var(--card)";
  } else if (isChild) {
    bg = "var(--card)";
    borderColor = "var(--mod-accent-line)";
    glyphColor = "var(--mod-accent)";
  } else {
    bg = "var(--mod-accent)";
    borderColor = "var(--mod-accent)";
    glyphColor = "#FFFFFF";
  }

  // Base drop shadow; selected adds a 3px red ring; else default adds a 2px accent ring.
  const baseShadow = "0 2px 6px rgba(0,0,0,0.28)";
  const boxShadow = selected
    ? `${baseShadow}, 0 0 0 3px var(--sv2-pin-sel-ring)`
    : isDefault && !isDisabled
    ? `${baseShadow}, 0 0 0 2px var(--mod-accent-line)`
    : baseShadow;

  const glyphSize = isChild ? 5 : 7;

  return (
    <OverlayViewF
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={getPixelPositionOffset}
    >
      <div
        className={selected ? "sv2-pin-selected" : undefined}
        onClick={(e) => {
          // Stop the map's background onClick (deselect) from also firing.
          e.stopPropagation();
          onClick(location);
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          position: "relative",
          width: size,
          height: size,
          cursor: "pointer",
          // Selected pins render at full opacity even if the location is disabled.
          opacity: isDisabled && !selected ? 0.55 : 1,
          zIndex: selected ? 1 : undefined,
        }}
      >
        {/* Name label chip on hover — above the pin. */}
        {hover && (
          <div
            style={{
              position: "absolute",
              bottom: "calc(100% + 6px)",
              left: "50%",
              transform: "translateX(-50%)",
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              lineHeight: 1.3,
              color: "var(--ink)",
              background: "var(--card)",
              border: "1px solid var(--border)",
              borderRadius: 6,
              padding: "3px 7px",
              whiteSpace: "nowrap",
              boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
              pointerEvents: "none",
              zIndex: 2,
            }}
          >
            {location.name}
          </div>
        )}

        {/* Teardrop pin — classic 50% 50% 50% 0 + rotate(-45deg) trick. */}
        <div
          style={{
            width: size,
            height: size,
            transform: "rotate(-45deg)",
            borderRadius: "50% 50% 50% 0",
            background: bg,
            border: `1px solid ${borderColor}`,
            boxShadow,
            display: "grid",
            placeItems: "center",
          }}
        >
          <span
            style={{
              width: glyphSize,
              height: glyphSize,
              borderRadius: "50%",
              background: glyphColor,
            }}
          />
        </div>
      </div>
    </OverlayViewF>
  );
};

export default React.memo(LocationsMapPinV2);
